<template>
  <div
    ref="grid"
    class="grid base-grid"
    :style="styles"
  >
    <template v-for="(item, index) in items">
      <div
        v-if="Object.keys(item).length"
        ref="item"
        :key="JSON.stringify(item)"
        class="base-grid--cell"
        :class="`base-grid--${item.name}`"
        :style="getItemStyles(item)"
      >
        <div class="base-grid--item">
          <slot
            v-if="namedSlots[item.name]"
            :name="item.name"
            :item="item"
            :index="index"
          />
          <slot
            v-else
            :item="item"
            :index="index"
          />
        </div>
      </div>
    </template>
  </div>
</template>

<script>
export default {
  name: 'BaseGrid',
  props: {
    /**
     * Grid Items need a name property.
     * Name property is used to enable named slots and template areas.
     * Items can also have a span property to specify the number of columns an item should span.
     */
    items: {
      type: Array,
      required: true,
    },
    /**
     * Grid gap, can be a number or a string with units.
     */
    gap: {
      type: [Number, String],
      default: 32,
    },
    /**
     * Number of columns, ignored if template, breakpoints or layout are provided. Cols are evenly distributed across the grid.
     */
    cols: {
      type: Number,
      default: null,
    },
    /**
     * Minimum number of columns, ignored if template, breakpoints or layout are provided. If minCols is smaller than items.length, cols will not fill the grid.
     */
    minCols: {
      type: Number,
      default: null,
    },
    /**
     * grid-template-columns layout, if provided, overrides cols.
     */
    layout: {
      type: String,
      default: null,
    },
    /**
     * Array if template is provided, object with minWidth and cols keys if not, overrides cols.
     */
    breakpoints: {
      type: [Array, Object],
      default: () => ([]),
    },
    /**
     * grid-template-areas template, multiple templates can be provided for different breakpoints. Overrides cols.
     */
    template: {
      type: Array,
      default: () => ([]),
    },
    masonry: {
      type: Boolean,
      default: false,
    },
    /**
     * if auto-flow is enabled the items will be placed automatically in the grid.
     */
    autoFlow: {
      type: [Boolean, String],
      default: false,
      validator: (value) => {
        const validValues = [true, false, 'column', 'row', 'wrap', 'column-reverse', 'row-reverse', 'wrap-reverse', 'inherit', 'initial', 'unset', 'nowrap', 'revert'];

        return validValues.includes(value);
      },
    },
    /**
     * if auto-fill is enabled the grid will fill the available space.
     */
    autoFill: {
      type: Boolean,
      default: false,
    },
  },
  setup(props, { slots }) {
    const itemsHaveName = props.items.every((item) => item.name);
    const namedSlots = Object.entries(slots).filter(([name]) => name !== 'default').reduce((acc, [name, slot]) => {
      acc[name] = slot;
      return acc;
    }, {});

    if (!itemsHaveName) {
      props.items.filter((item) => !item.name).forEach((item) => {
        if (Object.keys(item).length) {
          console.warn('<BaseGrid>: All items must have a name property. Missing name: ', item);
        }
      });
    }

    return {
      namedSlots,
    };
  },
  data() {
    return {
      currentCols: 0,
      currentBreakpointIndex: 0,
      observer: null,
    };
  },
  computed: {
    styles() {
      const gap = typeof this.gap === 'number' ? `${this.gap}px` : this.gap;

      if (this.masonry) {
        return {
          display: 'block !important',
          position: 'relative',
        };
      }

      if (this.autoFlow) {
        return {
          display: 'flex !important',
          flexFlow: this.autoFlow === true ? 'wrap' : this.autoFlow,
          gap,
        };
      }

      let gridTemplateAreas = '';
      if (this.hasTemplate && this.template.every((template) => template instanceof Array)) {
        gridTemplateAreas = this.template[this.currentBreakpointIndex].map((row) => `"${row}"`).join(' ');
      } else if (this.hasTemplate && typeof this.template[0] === 'string') {
        gridTemplateAreas = this.template.join(' ');
      }

      if (this.hasTemplate) {
        const gridTemplateColumns = this.template[this.currentBreakpointIndex].map((row) => row.split(' ').length).reduce((acc, curr) => (curr > acc ? curr : acc), 0);

        return {
          gridTemplateAreas,
          gridTemplateColumns: this.layout || `repeat(${gridTemplateColumns}, 1fr)`,
          gap,
        };
      }

      if (this.layout) {
        const gridTemplateColumns = this.items.length < this.currentCols ? `repeat(${this.items.length}, 1fr)` : this.layout;

        return {
          gridTemplateColumns,
          gap,
        };
      }

      let gridTemplateColumns = !this.minCols && this.items.length < this.currentCols ? `repeat(${this.items.length}, 1fr)` : `repeat(${this.currentCols}, 1fr)`;

      if (this.breakpoints.length && this.items.length < this.currentCols && !this.autoFill) {
        gridTemplateColumns = `repeat(${this.breakpoints[this.getCurrentBreakpointIndex()].cols}, 1fr)`;
      }

      return {
        gridTemplateColumns,
        gap,
      };
    },
    hasTemplate() {
      return this.template.length;
    },
    hasLayout() {
      return this.layout;
    },
  },
  watch: {
    breakpoints() {
      this.setCurrentBreakpointIndex();
      this.setCurrentCols();
    },
  },
  mounted() {
    if (this.masonry) {
      const { grid } = this.$refs;
      const items = Array.from(grid.querySelectorAll('.base-grid--item'));

      this.handleMasonry();

      items.forEach((item) => {
        item.addEventListener('click', () => {
          this.$nextTick(() => {
            this.updateMasonry(item);
          });
        });
      });

      window.addEventListener('resize', this.handleMasonry);
      window.addEventListener('load', this.handleMasonry);
      window.addEventListener('ready', this.handleMasonry);
      this.$nextTick(this.handleMasonry);

      let timesMasonryRunned = 0;
      // Kept on the instance so beforeDestroy can stop it. As a local const it outlived the
      // component and kept calling handleMasonry() on torn-down refs for up to 1.25 s.
      this.masonryInterval = setInterval(() => {
        this.handleMasonry();

        timesMasonryRunned += 1;
        if (timesMasonryRunned >= 10) {
          clearInterval(this.masonryInterval);
        }
      }, 125);
    }
  },
  created() {
    this.setCurrentBreakpointIndex();
    this.setCurrentCols();

    window.addEventListener('resize', () => {
      this.setCurrentBreakpointIndex();
      this.setCurrentCols();
    });
  },
  updated() {
    if (this.masonry) {
      this.handleMasonry();
    }
  },
  beforeDestroy() {
    clearInterval(this.masonryInterval);

    const { grid } = this.$refs;
    const items = grid ? Array.from(grid.querySelectorAll('.base-grid--item')) : [];

    items.forEach((item) => {
      item.removeEventListener('click', () => {
        this.updateMasonry(item);
      });
    });
    window.removeEventListener('resize', () => {
      this.setCurrentBreakpointIndex();
      this.setCurrentCols();
    });

    window.removeEventListener('resize', this.handleMasonry);
    window.removeEventListener('load', this.handleMasonry);
    window.removeEventListener('ready', this.handleMasonry);
  },
  methods: {
    updateMasonry(item, previousMetrics = []) {
      const { scrollHeight, scrollWidth } = item;

      const countSameMetricFrames = previousMetrics.filter((metric) => metric[0] === scrollHeight && metric[1] === scrollWidth).length;

      if (countSameMetricFrames >= 30) return;

      this.handleMasonry();

      window.requestAnimationFrame(() => {
        this.updateMasonry(item, [...previousMetrics, [scrollHeight, scrollWidth]]);
      });
    },
    handleMasonry() {
      /* eslint-disable no-param-reassign */
      const { grid } = this.$refs;
      const children = this.$refs.item;

      // A queued timer or an in-flight window listener can still reach this after teardown.
      if (!grid || !children) {
        return;
      }

      children.forEach((item) => {
        item.style.position = 'absolute';
        item.style.width = `calc((100% - ${(this.currentCols - 1) * this.gap}px) / ${this.currentCols})`;
      });

      const items = Array.from(grid.querySelectorAll('.base-grid--item'));
      const lastRow = [];
      const itemDimensions = items.map((item) => ({
        width: item.offsetWidth,
        height: item.scrollHeight,
      })).reduce((acc, curr, index) => {
        acc[index] = {
          width: curr.width,
          height: curr.height,
          x: (index % this.currentCols) * (curr.width + this.gap),
          y: lastRow[index % this.currentCols]
            ? lastRow[index % this.currentCols].height + lastRow[index % this.currentCols].y + this.gap
            : 0,
        };
        lastRow[index % this.currentCols] = acc[index];
        return acc;
      }, []);

      children.forEach((item, index) => {
        item.style.transform = `translate(${itemDimensions[index].x}px, ${itemDimensions[index].y}px)`;
        item.style.height = `${itemDimensions[index].height}px`;
      });

      const gridCols = itemDimensions.reduce((acc, curr) => {
        if (!acc[curr.x]) {
          acc[curr.x] = [];
        }
        acc[curr.x].push(curr);
        return acc;
      }, {});
      const gridHeight = Object.values(gridCols).reduce((acc, curr) => {
        const lastItem = curr[curr.length - 1];
        const height = lastItem.y + lastItem.height;
        return height > acc ? height : acc;
      }, 0);

      grid.style.height = `${gridHeight}px`;
      /* eslint-enable no-param-reassign */
    },
    getItemStyles(item) {
      if (this.hasTemplate) {
        return {
          gridArea: item.name,
        };
      }
      if (item.span) {
        return {
          gridColumn: `span ${item.span > this.currentCols ? this.currentCols : item.span}`,
        };
      }
      return {};
    },
    setCurrentCols() {
      this.currentCols = this.getCurrentCols();
    },
    setCurrentBreakpointIndex() {
      this.currentBreakpointIndex = this.getCurrentBreakpointIndex();
    },
    getCurrentBreakpointIndex() {
      const currentBreakpoint = [...this.breakpoints].reverse().find((breakpoint) => {
        if (breakpoint instanceof Object) {
          return window.innerWidth >= breakpoint.minWidth;
        }

        return window.innerWidth >= breakpoint;
      }) || this.breakpoints[0];
      return this.breakpoints.indexOf(currentBreakpoint);
    },
    getCurrentCols() {
      if (this.layout) {
        return this.layout.split(' ').length;
      }

      const { currentBreakpointIndex } = this;

      if (this.hasTemplate && this.template.every((template) => template instanceof Array)) {
        if (currentBreakpointIndex === -1) {
          console.warn('<BaseGrid>: Breakpoints must be an array of numbers if template is provided.');
          return this.cols;
        }
        if (this.template.length < this.breakpoints.length) {
          console.warn('<BaseGrid>: Breakpoints and template must have the same amount of items.');
          return this.cols;
        }
        return this.template[currentBreakpointIndex][0].split(' ').length;
      }

      if (this.hasTemplate && typeof this.template[0] === 'string') {
        return this.template[0].split(' ').length;
      }

      if (this.breakpoints.length && this.breakpoints[currentBreakpointIndex] instanceof Object) {
        return this.breakpoints[currentBreakpointIndex].cols;
      }

      if (this.breakpoints.length) {
        console.warn('<BaseGrid>: Breakpoints must be an array of objects with minWidth and cols keys if template or layout are not provided.');
      }

      return this.minCols || this.cols;
    },
  },
};
</script>
